import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  EXPERIENCE_OPTIONS,
  FOOD_OPTIONS,
  GENDER_OPTIONS,
  LANGUAGES,
  NURSE_CLINICAL,
  NURSE_QUALIFICATIONS,
  NURSE_SKILLS,
  PATIENT_CONDITIONS,
  PHYSIO_DURATION,
  PHYSIO_FREQUENCY,
  PHYSIO_REQUIREMENTS,
  CONSULTATION_TYPES,
  RELIGION_OPTIONS,
  SALARY_HOURLY_MIN,
  SALARY_MONTHLY_MIN,
  SALARY_HOURLY_MAX,
  SALARY_MONTHLY_MAX,
  SHIFT_TYPES,
  SKILLS_COMMON,
  SLEEP_OPTIONS,
  AGE_GROUPS,
} from "../../config/marketplaceFilterEngine";
import "./MarketplaceFilterPanel.css";

function toggleInList(list, value) {
  const set = new Set(list || []);
  if (set.has(value)) set.delete(value);
  else set.add(value);
  return [...set];
}

function FilterChevron({ open = false }) {
  return (
    <svg
      className={`mk-filter__chevron${open ? " is-open" : ""}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function FilterDropdown({ label, placeholder, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setPanelStyle(null);
      return undefined;
    }
    function place() {
      const rect = triggerRef.current.getBoundingClientRect();
      const gap = 4;
      const maxH = Math.min(176, Math.max(120, window.innerHeight - rect.bottom - 12));
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const openUp = spaceBelow < 140 && rect.top > spaceBelow;
      setPanelStyle({
        position: "fixed",
        left: rect.left,
        width: rect.width,
        top: openUp ? undefined : rect.bottom + gap,
        bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
        maxHeight: openUp ? Math.min(176, rect.top - 12) : maxH,
        zIndex: 1200,
      });
    }
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e) {
      const t = e.target;
      if (wrapRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const values = selected || [];
  const summary =
    values.length === 0
      ? placeholder
      : values.length === 1
        ? values[0]
        : `${values.length} selected`;

  const panel =
    open && panelStyle
      ? createPortal(
          <div
            ref={panelRef}
            className="mk-filter__dropdown-panel mk-filter__dropdown-panel--portal"
            role="listbox"
            style={panelStyle}
          >
            {options.map((opt) => {
              const value = typeof opt === "string" ? opt : opt.value;
              const text = typeof opt === "string" ? opt : opt.label;
              const checked = values.includes(value);
              return (
                <label key={value} className="mk-filter__check">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onChange(toggleInList(values, value))}
                  />
                  <span>{text}</span>
                </label>
              );
            })}
          </div>,
          document.body
        )
      : null;

  return (
    <div className={`mk-filter__dropdown${open ? " is-open" : ""}`} ref={wrapRef}>
      <span className="mk-filter__dropdown-label">{label}</span>
      <button
        ref={triggerRef}
        type="button"
        className="mk-filter__dropdown-trigger"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="mk-filter__dropdown-value">{summary}</span>
        <FilterChevron open={open} />
      </button>
      {panel}
    </div>
  );
}

export default function MarketplaceFilterPanel({
  careType,
  filters,
  onChange,
  onClear,
  planType,
  lockedGender = null,
}) {
  const f = filters || {};
  const set = (patch) => onChange({ ...f, ...patch });
  const isNurse = careType === "nurses";
  const isPhysio = careType === "physio";
  const genderLocked = lockedGender === "male" || lockedGender === "female";
  const salaryServiceType =
    planType === "monthly"
      ? "monthly"
      : planType === "daily" || planType === "weekly"
        ? "hourly"
        : f.salaryServiceType || "both";
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  return (
    <>
      <button
        type="button"
        className="mk-filter-mobile-toggle"
        aria-label={mobileOpen ? "Close filters menu" : "Open filters menu"}
        aria-expanded={mobileOpen}
        onClick={() => setMobileOpen((open) => !open)}
      >
        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <circle cx="4" cy="5" r="1.35" fill="currentColor" />
          <circle cx="4" cy="10" r="1.35" fill="currentColor" />
          <circle cx="4" cy="15" r="1.35" fill="currentColor" />
          <rect x="7.5" y="4.1" width="9" height="1.8" rx="0.9" fill="currentColor" />
          <rect x="7.5" y="9.1" width="9" height="1.8" rx="0.9" fill="currentColor" />
          <rect x="7.5" y="14.1" width="9" height="1.8" rx="0.9" fill="currentColor" />
        </svg>
        <span>Filters</span>
      </button>

      {mobileOpen ? (
        <button
          type="button"
          className="mk-filter-overlay"
          aria-label="Close filters overlay"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}

      <aside
        className={`mk-filter-panel landing-filters landing-filters--engine${
          mobileOpen ? " mk-filter-panel--open" : ""
        }`}
      >
      <div className="landing-filters__head">
        <h2>
          <svg
            className="landing-filters__head-icon"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <circle cx="4" cy="5" r="1.35" fill="currentColor" />
            <circle cx="4" cy="10" r="1.35" fill="currentColor" />
            <circle cx="4" cy="15" r="1.35" fill="currentColor" />
            <rect x="7.5" y="4.1" width="9" height="1.8" rx="0.9" fill="currentColor" />
            <rect x="7.5" y="9.1" width="9" height="1.8" rx="0.9" fill="currentColor" />
            <rect x="7.5" y="14.1" width="9" height="1.8" rx="0.9" fill="currentColor" />
          </svg>
          Filters
        </h2>
        <button type="button" className="landing-filters__clear" onClick={onClear}>
          Clear All
        </button>
        <button
          type="button"
          className="mk-filter-panel__close"
          aria-label="Close filters"
          onClick={() => setMobileOpen(false)}
        >
          ✕
        </button>
      </div>

      {isNurse ? (
        <>
          <FilterDropdown
            label="Clinical Requirement"
            placeholder="Select requirements"
            options={NURSE_CLINICAL}
            selected={f.nurseClinical}
            onChange={(nurseClinical) => set({ nurseClinical })}
          />
          <FilterDropdown
            label="Nurse Qualification"
            placeholder="Select qualifications"
            options={NURSE_QUALIFICATIONS}
            selected={f.nurseQualifications}
            onChange={(nurseQualifications) => set({ nurseQualifications })}
          />
          <FilterDropdown
            label="Skills (Nurse-Specific)"
            placeholder="Select skills"
            options={NURSE_SKILLS}
            selected={f.nurseSkills}
            onChange={(nurseSkills) => set({ nurseSkills })}
          />
        </>
      ) : null}

      {isPhysio ? (
        <>
          <FilterDropdown
            label="Physiotherapist Requirement"
            placeholder="Select requirements"
            options={PHYSIO_REQUIREMENTS}
            selected={f.physioRequirements}
            onChange={(physioRequirements) => set({ physioRequirements })}
          />
          <label className="mk-filter__select-label">
            Consultation type
            <select
              value={f.consultationType || ""}
              onChange={(e) => set({ consultationType: e.target.value })}
            >
              <option value="">Any</option>
              {CONSULTATION_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="mk-filter__select-label">
            Visit Frequency
            <select value={f.physioFrequency || ""} onChange={(e) => set({ physioFrequency: e.target.value })}>
              <option value="">Select frequency</option>
              {PHYSIO_FREQUENCY.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
          <label className="mk-filter__select-label">
            Session Duration
            <select value={f.physioDuration || ""} onChange={(e) => set({ physioDuration: e.target.value })}>
              <option value="">Select duration</option>
              {PHYSIO_DURATION.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
        </>
      ) : null}

      <FilterDropdown
        label="Patient Condition"
        placeholder="Select conditions"
        options={PATIENT_CONDITIONS}
        selected={f.patientConditions}
        onChange={(patientConditions) => set({ patientConditions })}
      />

      <FilterDropdown
        label="Language Spoken"
        placeholder="Select Languages"
        options={LANGUAGES}
        selected={f.languages}
        onChange={(languages) => set({ languages })}
      />

      <fieldset className="mk-filter__fieldset landing-filters__gender">
        <legend>Gender Preference</legend>
        {genderLocked ? (
          <p className="landing-filters__hint">
            Matched to patient gender ({lockedGender} caregivers only).
          </p>
        ) : null}
        <div className="landing-filters__gender-row">
          {GENDER_OPTIONS.map(({ value, label }) => {
            const lockedOut = genderLocked && value !== lockedGender;
            return (
              <button
                key={value}
                type="button"
                className={f.gender === value ? "is-active" : ""}
                disabled={lockedOut}
                aria-disabled={lockedOut}
                title={
                  lockedOut
                    ? `Only ${lockedGender} caregivers are shown for this patient`
                    : undefined
                }
                onClick={() => {
                  if (lockedOut) return;
                  set({ gender: value });
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </fieldset>

      <label className="mk-filter__select-label">
        Age Group
        <select value={f.ageGroup || ""} onChange={(e) => set({ ageGroup: e.target.value })}>
          <option value="">Select age group</option>
          {AGE_GROUPS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      <label className="mk-filter__select-label">
        Years of Experience
        <select
          value={String(f.minExperience || 0)}
          onChange={(e) => set({ minExperience: Number(e.target.value) })}
        >
          <option value="0">Select Experience</option>
          {EXPERIENCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      <div className="mk-filter__toggle mk-filter__toggle--trust">
        <span className="mk-filter__toggle-label" id="mk-filter-verified-label">
          Only Verified Caregivers
        </span>
        <button
          type="button"
          className={`mk-filter__switch${f.verifiedOnly ? " is-on" : ""}`}
          role="switch"
          aria-checked={Boolean(f.verifiedOnly)}
          aria-labelledby="mk-filter-verified-label"
          onClick={() => set({ verifiedOnly: !f.verifiedOnly })}
        >
          <span className="mk-filter__switch-knob" aria-hidden="true" />
        </button>
      </div>

      <button
        type="button"
        className="landing-filters__more mk-filter__more-toggle"
        aria-expanded={f.moreFiltersOpen}
        onClick={() => set({ moreFiltersOpen: !f.moreFiltersOpen })}
      >
        <span>More Filters</span>
        <FilterChevron open={f.moreFiltersOpen} />
      </button>

      {f.moreFiltersOpen ? (
        <div className="mk-filter__more">
          <FilterDropdown
            label="Skills"
            placeholder="Select skills"
            options={SKILLS_COMMON}
            selected={f.skills}
            onChange={(skills) => set({ skills })}
          />

          <label className="mk-filter__select-label">
            Religion Preference
            <select value={f.religion || "any"} onChange={(e) => set({ religion: e.target.value })}>
              {RELIGION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label className="mk-filter__select-label">
            Food Preference
            <select
              value={f.foodPreference || "any"}
              onChange={(e) => set({ foodPreference: e.target.value })}
            >
              {FOOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label className="mk-filter__select-label">
            Sleep Arrangement
            <select
              value={f.sleepArrangement || ""}
              onChange={(e) => set({ sleepArrangement: e.target.value })}
            >
              <option value="">Any</option>
              {SLEEP_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label className="mk-filter__toggle">
            <input
              type="checkbox"
              checked={Boolean(f.availableNow)}
              onChange={(e) => set({ availableNow: e.target.checked })}
            />
            <span>Available Now</span>
          </label>

          <label className="mk-filter__select-label">
            Shift Type
            <select value={f.shiftType || ""} onChange={(e) => set({ shiftType: e.target.value })}>
              <option value="">Any shift</option>
              {SHIFT_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label className="mk-filter__select-label">
            Minimum Rating
            <select
              value={String(f.minRating || 0)}
              onChange={(e) => set({ minRating: Number(e.target.value) })}
            >
              <option value="0">Any rating</option>
              <option value="3">3+ stars</option>
              <option value="4">4+ stars</option>
              <option value="4.5">4.5+ stars</option>
            </select>
          </label>

          <fieldset className="mk-filter__fieldset mk-filter__salary">
            <legend>Salary Budget Range</legend>
            {salaryServiceType !== "monthly" ? (
              <>
                <div className="landing-filters__salary-labels">
                  <span>Hourly</span>
                  <span>
                    ₹{Number(f.salaryHourlyMin ?? SALARY_HOURLY_MIN).toLocaleString("en-IN")} –{" "}
                    ₹{Number(f.salaryHourlyMax ?? SALARY_HOURLY_MAX).toLocaleString("en-IN")} / hr
                  </span>
                </div>
                <div className="mk-filter__salary-range">
                  <input
                    type="range"
                    min={SALARY_HOURLY_MIN}
                    max={SALARY_HOURLY_MAX}
                    step="100"
                    value={f.salaryHourlyMin ?? SALARY_HOURLY_MIN}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      set({ salaryHourlyMin: Math.min(val, (f.salaryHourlyMax ?? SALARY_HOURLY_MAX) - 100) });
                    }}
                    aria-label="Minimum hourly salary"
                  />
                  <input
                    type="range"
                    min={SALARY_HOURLY_MIN}
                    max={SALARY_HOURLY_MAX}
                    step="100"
                    value={f.salaryHourlyMax ?? SALARY_HOURLY_MAX}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      set({ salaryHourlyMax: Math.max(val, (f.salaryHourlyMin ?? SALARY_HOURLY_MIN) + 100) });
                    }}
                    aria-label="Maximum hourly salary"
                  />
                </div>
              </>
            ) : null}
            {salaryServiceType !== "hourly" ? (
              <>
                <div className="landing-filters__salary-labels">
                  <span>Monthly</span>
                  <span>
                    ₹{Number(f.salaryMonthlyMin ?? SALARY_MONTHLY_MIN).toLocaleString("en-IN")} –{" "}
                    ₹{Number(f.salaryMonthlyMax ?? SALARY_MONTHLY_MAX).toLocaleString("en-IN")} / mo
                  </span>
                </div>
                <div className="mk-filter__salary-range">
                  <input
                    type="range"
                    min={SALARY_MONTHLY_MIN}
                    max={SALARY_MONTHLY_MAX}
                    step="1000"
                    value={f.salaryMonthlyMin ?? SALARY_MONTHLY_MIN}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      set({ salaryMonthlyMin: Math.min(val, (f.salaryMonthlyMax ?? SALARY_MONTHLY_MAX) - 1000) });
                    }}
                    aria-label="Minimum monthly salary"
                  />
                  <input
                    type="range"
                    min={SALARY_MONTHLY_MIN}
                    max={SALARY_MONTHLY_MAX}
                    step="1000"
                    value={f.salaryMonthlyMax ?? SALARY_MONTHLY_MAX}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      set({ salaryMonthlyMax: Math.max(val, (f.salaryMonthlyMin ?? SALARY_MONTHLY_MIN) + 1000) });
                    }}
                    aria-label="Maximum monthly salary"
                  />
                </div>
              </>
            ) : null}
          </fieldset>
        </div>
      ) : null}
      </aside>
    </>
  );
}
